import {
	ApolloClient,
	ApolloLink,
	from,
	HttpLink,
	InMemoryCache,
	NormalizedCacheObject,
	ServerError,
} from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import * as authService from 'services/auth'
import { clearStorageKeepingPendingCheckouts } from 'utils/checkout-recovery'

export default class Apollo {
	private static instance: Apollo
	private isRedirecting = false

	public client: ApolloClient<NormalizedCacheObject>

	httpLink = new HttpLink({
		uri: '/api/graphql',
		headers: { 'Content-Type': 'application/json' },
		fetch: (uri, options) => {
			return fetch(uri, options).catch(error => {
				if (this.isRedirecting) {
					return new Response(JSON.stringify({ data: null }), {
						status: 200,
						headers: { 'Content-Type': 'application/json' },
					})
				}
				throw error
			})
		},
	})

	authLink = new ApolloLink((operation, forward) => {
		const token = authService.getToken()
		operation.setContext(({ headers = {} }) => ({
			headers: {
				...headers,
				authorization: token ? `Bearer ${token}` : '',
			},
		}))
		return forward(operation)
	})

	networkErrorLink = onError(({ networkError, operation }) => {
		if (this.isRedirecting || !networkError) return

		if ((networkError as ServerError)?.statusCode === 401) {
			this.handleAuthError('Unauthorized access attempt (network)', operation)
		}
	})

	errorHandlingLink = new ApolloLink((operation, forward) => {
		return forward(operation).map(response => {
			if (this.isRedirecting) {
				delete response.errors
				return response
			}

			if (response.errors) {
				const hasAuthError = response.errors.some(
					error =>
						['AUTH_NOT_AUTHORIZED', 'AUTH_NOT_AUTHENTICATED'].includes(error.extensions?.code as string) ||
						error.message === 'Unauthorized'
				)

				if (hasAuthError) {
					this.handleAuthError('Unauthorized access attempt', operation)
				}
			}
			return response
		})
	})

	private handleAuthError(_message: string, _operation: any) {
		this.isRedirecting = true
		this.client.stop()

		const isSignedUp = localStorage.getItem('isSignedUp') === 'true'
		clearStorageKeepingPendingCheckouts()

		const currentUrl = window.location.pathname + window.location.search + window.location.hash
		const authPage = isSignedUp ? 'login' : 'sign-up'

		// Extract emailAddress from query string if present
		const urlParams = new URLSearchParams(window.location.search)
		const emailAddress = urlParams.get('emailAddress')

		// Build redirect URL with 'to' parameter and optionally 'email' parameter
		const redirectParams = new URLSearchParams({ to: currentUrl })
		if (emailAddress) {
			redirectParams.set('email', emailAddress)
		}

		setTimeout(() => {
			window.location.href = `/auth/${authPage}?${redirectParams.toString()}`
		}, 0)
	}

	private constructor() {
		this.client = new ApolloClient({
			link: from([this.authLink, this.networkErrorLink, this.errorHandlingLink, this.httpLink]),
			cache: new InMemoryCache(),
			defaultOptions: {
				watchQuery: { fetchPolicy: 'no-cache' },
				query: { fetchPolicy: 'no-cache' },
				mutate: {},
			},
		})

		this.installGlobalErrorHandlers()
	}

	private installGlobalErrorHandlers() {
		window.addEventListener('unhandledrejection', event => {
			if (this.isRedirecting) event.preventDefault()
		})

		window.addEventListener('error', event => {
			if (this.isRedirecting) event.preventDefault()
		})
	}

	static getInstance(): Apollo {
		if (!this.instance) {
			this.instance = new Apollo()
		}
		return this.instance
	}

	async clearAllCache(): Promise<void> {
		try {
			this.client.stop()
			await this.client.cache.reset()
			await this.client.clearStore()
		} catch (error) {
			console.error('Error clearing Apollo cache:', error)
			throw error
		}
	}

	async clearUserCache(): Promise<void> {
		try {
			this.client.cache.evict({ fieldName: 'me' })
			this.client.cache.evict({ fieldName: 'user' })
			this.client.cache.evict({ fieldName: 'users' })

			const cacheData = this.client.cache.extract()
			Object.keys(cacheData).forEach(key => {
				if (key.startsWith('User:')) {
					this.client.cache.evict({ id: key })
				}
			})

			this.client.cache.gc()
		} catch (error) {
			console.error('Error clearing user cache:', error)
			throw error
		}
	}

	async hardReset(): Promise<void> {
		return this.clearAllCache()
	}
}
